import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet,
  Building2,
  Plus,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  RefreshCw,
  DollarSign,
  FileTemplate,
  Edit2,
  Trash2,
  Copy
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const Tesoreria = () => {
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [activeTab, setActiveTab] = useState("resumen");
  
  const [modalCuenta, setModalCuenta] = useState(false);
  const [modalMovimiento, setModalMovimiento] = useState(false);
  const [modalEditCuenta, setModalEditCuenta] = useState(false);
  const [cuentaEditar, setCuentaEditar] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [modalPlantilla, setModalPlantilla] = useState(false);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [formPlantilla, setFormPlantilla] = useState({
    nombre: "", descripcion: "", tipo_operacion: "INGRESO",
    cuenta_tesoreria_id: null, cuenta_destino_id: null,
    cuenta_contable_debe_id: null, cuenta_contable_haber_id: null,
    concepto_default: ""
  });
  
  const [formCuenta, setFormCuenta] = useState({
    tipo: "CAJA",
    codigo: "",
    nombre: "",
    banco_id: null,
    numero_cuenta: "",
    cbu: "",
    saldo_inicial: 0,
    moneda_id: ""
  });
  
  const [formMovimiento, setFormMovimiento] = useState({
    cuenta_tesoreria_id: "",
    fecha: new Date().toISOString().split("T")[0],
    tipo: "INGRESO",
    concepto: "",
    importe: 0,
    numero_referencia: ""
  });

  const fetchCuentas = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria");
      setCuentas(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchBancos = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/bancos");
      setBancos(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchMonedas = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/monedas");
      setMonedas(response.data);
      const monedaDefault = response.data.find(m => m.es_default);
      if (monedaDefault) {
        setFormCuenta(prev => ({ ...prev, moneda_id: monedaDefault.id.toString() }));
      }
    } catch (error) {
      console.error("Error cargando monedas:", error);
    }
  };

  const fetchMovimientos = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/movimientos-tesoreria?limit=50");
      setMovimientos(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchPlantillas = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/plantillas-tesoreria");
      setPlantillas(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchCuentasContables = async () => {
    try {
      const response = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas?solo_imputables=true");
      setCuentasContables(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCuentas(), fetchBancos(), fetchMonedas(), fetchMovimientos(), fetchPlantillas(), fetchCuentasContables()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSaveCuenta = async () => {
    if (!formCuenta.codigo || !formCuenta.nombre) {
      toast.error("Complete los campos requeridos");
      return;
    }
    if (!formCuenta.moneda_id) {
      toast.error("Seleccione una moneda");
      return;
    }
    setSaving(true);
    try {
      await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria", {
        ...formCuenta,
        banco_id: formCuenta.tipo === "BANCO" ? parseInt(formCuenta.banco_id) : null,
        moneda_id: parseInt(formCuenta.moneda_id)
      });
      toast.success("Cuenta creada exitosamente");
      setModalCuenta(false);
      fetchCuentas();
      const monedaDefault = monedas.find(m => m.es_default);
      setFormCuenta({ 
        tipo: "CAJA", codigo: "", nombre: "", banco_id: null, 
        numero_cuenta: "", cbu: "", saldo_inicial: 0, 
        moneda_id: monedaDefault ? monedaDefault.id.toString() : "" 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setSaving(false);
    }
  };

  const handleEditCuenta = (cuenta) => {
    setCuentaEditar(cuenta);
    setModalEditCuenta(true);
  };

  const handleSaveEditCuenta = async () => {
    if (!cuentaEditar) return;
    setSaving(true);
    try {
      await axios.put(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria/" + cuentaEditar.id, {
        nombre: cuentaEditar.nombre,
        numero_cuenta: cuentaEditar.numero_cuenta,
        cbu: cuentaEditar.cbu
      });
      toast.success("Cuenta actualizada exitosamente");
      setModalEditCuenta(false);
      setCuentaEditar(null);
      fetchCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar cuenta");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMovimiento = async () => {
    if (!formMovimiento.cuenta_tesoreria_id || !formMovimiento.concepto || !formMovimiento.importe) {
      toast.error("Complete los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/movimientos-tesoreria", {
        ...formMovimiento,
        cuenta_tesoreria_id: parseInt(formMovimiento.cuenta_tesoreria_id),
        importe: parseFloat(formMovimiento.importe)
      });
      toast.success("Movimiento registrado exitosamente");
      setModalMovimiento(false);
      fetchMovimientos();
      fetchCuentas();
      setFormMovimiento({
        cuenta_tesoreria_id: "",
        fecha: new Date().toISOString().split("T")[0],
        tipo: "INGRESO",
        concepto: "",
        importe: 0,
        numero_referencia: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar movimiento");
    } finally {
      setSaving(false);
    }
  };

  const handleAplicarPlantilla = (plantilla) => {
    setFormMovimiento({
      ...formMovimiento,
      tipo: plantilla.tipo_operacion,
      concepto: plantilla.concepto_default || "",
      cuenta_tesoreria_id: plantilla.cuenta_tesoreria_id ? plantilla.cuenta_tesoreria_id.toString() : "",
    });
    toast.success("Plantilla '" + plantilla.nombre + "' aplicada");
  };

  const handleSavePlantilla = async () => {
    if (!formPlantilla.nombre || !formPlantilla.tipo_operacion) {
      toast.error("Complete nombre y tipo de operacion");
      return;
    }
    setSaving(true);
    try {
      if (plantillaEditando) {
        await axios.put(API_URL + "/api/erp/empresas/" + TENANT_ID + "/plantillas-tesoreria/" + plantillaEditando.id, formPlantilla);
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/plantillas-tesoreria", formPlantilla);
        toast.success("Plantilla creada");
      }
      setModalPlantilla(false);
      setPlantillaEditando(null);
      setFormPlantilla({ nombre: "", descripcion: "", tipo_operacion: "INGRESO", cuenta_tesoreria_id: null, cuenta_destino_id: null, cuenta_contable_debe_id: null, cuenta_contable_haber_id: null, concepto_default: "" });
      fetchPlantillas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const handleEditPlantilla = (p) => {
    setPlantillaEditando(p);
    setFormPlantilla({
      nombre: p.nombre, descripcion: p.descripcion || "", tipo_operacion: p.tipo_operacion,
      cuenta_tesoreria_id: p.cuenta_tesoreria_id, cuenta_destino_id: p.cuenta_destino_id,
      cuenta_contable_debe_id: p.cuenta_contable_debe_id, cuenta_contable_haber_id: p.cuenta_contable_haber_id,
      concepto_default: p.concepto_default || ""
    });
    setModalPlantilla(true);
  };

  const handleDeletePlantilla = async (id) => {
    if (!window.confirm("Eliminar esta plantilla?")) return;
    await axios.delete(API_URL + "/api/erp/empresas/" + TENANT_ID + "/plantillas-tesoreria/" + id);
    toast.success("Plantilla eliminada");
    fetchPlantillas();
  };

  const formatCurrency = (value, simbolo) => {
    const sym = simbolo || "$";
    const formatted = new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return sym + " " + formatted;
  };

  const saldosPorMoneda = monedas.map(moneda => {
    const cuentasMoneda = cuentas.filter(c => c.moneda_id === moneda.id || (!c.moneda_id && moneda.es_default));
    const totalCajas = cuentasMoneda.filter(c => c.tipo === "CAJA").reduce((sum, c) => sum + parseFloat(c.saldo_actual || 0), 0);
    const totalBancos = cuentasMoneda.filter(c => c.tipo === "BANCO").reduce((sum, c) => sum + parseFloat(c.saldo_actual || 0), 0);
    return { moneda, totalCajas, totalBancos, total: totalCajas + totalBancos };
  }).filter(s => s.total !== 0 || s.moneda.es_default);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="erp-tesoreria">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Tesoreria</h1>
          <p className="text-gray-600">Gestion de cajas, bancos y movimientos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchCuentas(); fetchMovimientos(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="resumen" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <DollarSign className="w-4 h-4 mr-2" />Resumen
          </TabsTrigger>
          <TabsTrigger value="cuentas" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <Wallet className="w-4 h-4 mr-2" />Cuentas
          </TabsTrigger>
          <TabsTrigger value="movimientos" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <ArrowLeftRight className="w-4 h-4 mr-2" />Movimientos
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <Copy className="w-4 h-4 mr-2" />Plantillas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-6 mt-6">
          {saldosPorMoneda.map(({ moneda, totalCajas, totalBancos, total }) => (
            <div key={moneda.id} className="space-y-4">
              <h3 className="text-lg font-semibold text-[#1E3876] flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">{moneda.codigo}</span>
                {moneda.nombre}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="cao-card border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total en Cajas</p>
                          <p className="text-2xl font-bold text-[#1E3876]">{formatCurrency(totalCajas, moneda.simbolo)}</p>
                        </div>
                        <Wallet className="w-10 h-10 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Card className="cao-card border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total en Bancos</p>
                          <p className="text-2xl font-bold text-[#1E3876]">{formatCurrency(totalBancos, moneda.simbolo)}</p>
                        </div>
                        <Building2 className="w-10 h-10 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <Card className="cao-card border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Disponible</p>
                          <p className="text-2xl font-bold text-[#1E3876]">{formatCurrency(total, moneda.simbolo)}</p>
                        </div>
                        <DollarSign className="w-10 h-10 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>
          ))}
          <Card className="cao-card">
            <CardHeader><CardTitle className="text-[#1E3876]">Saldos por Cuenta</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cuentas.map((cuenta) => {
                  const moneda = monedas.find(m => m.id === cuenta.moneda_id) || monedas.find(m => m.es_default) || { simbolo: "$", codigo: "ARS" };
                  return (
                    <div key={cuenta.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {cuenta.tipo === "CAJA" ? <Wallet className="w-5 h-5 text-green-500" /> : <Building2 className="w-5 h-5 text-blue-500" />}
                        <div>
                          <p className="font-medium">{cuenta.nombre}</p>
                          <p className="text-sm text-gray-500">{cuenta.codigo}</p>
                        </div>
                        <Badge variant="outline" className="ml-2">{moneda.codigo}</Badge>
                      </div>
                      <p className="font-bold text-lg">{formatCurrency(cuenta.saldo_actual, moneda.simbolo)}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cuentas" className="mt-6">
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#1E3876]">Cuentas de Tesoreria</CardTitle>
              <Button onClick={() => setModalCuenta(true)} data-testid="btn-nueva-cuenta">
                <Plus className="w-4 h-4 mr-2" />Nueva Cuenta
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Moneda</TableHead>
                    <TableHead>Banco</TableHead>
                    <TableHead>CBU</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cuentas.map((cuenta) => {
                    const moneda = monedas.find(m => m.id === cuenta.moneda_id) || monedas.find(m => m.es_default) || { simbolo: "$", codigo: "ARS" };
                    return (
                      <TableRow key={cuenta.id}>
                        <TableCell>
                          <Badge className={cuenta.tipo === "CAJA" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}>{cuenta.tipo}</Badge>
                        </TableCell>
                        <TableCell className="font-mono">{cuenta.codigo}</TableCell>
                        <TableCell>{cuenta.nombre}</TableCell>
                        <TableCell><Badge variant="outline">{moneda.simbolo} {moneda.codigo}</Badge></TableCell>
                        <TableCell>{cuenta.banco_nombre || "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{cuenta.cbu || "-"}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(cuenta.saldo_actual, moneda.simbolo)}</TableCell>
                        <TableCell><Button variant="ghost" size="sm" onClick={() => handleEditCuenta(cuenta)}>Editar</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimientos" className="mt-6">
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#1E3876]">Movimientos de Tesoreria</CardTitle>
              <Button onClick={() => setModalMovimiento(true)} data-testid="btn-nuevo-movimiento">
                <Plus className="w-4 h-4 mr-2" />Nuevo Movimiento
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Concepto</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead className="text-right">Importe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movimientos.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell>{new Date(mov.fecha).toLocaleDateString("es-AR")}</TableCell>
                      <TableCell>{mov.cuenta_nombre}</TableCell>
                      <TableCell>
                        <Badge className={mov.tipo === "INGRESO" ? "bg-green-100 text-green-700" : mov.tipo === "EGRESO" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>
                          {mov.tipo === "INGRESO" && <ArrowUpCircle className="w-3 h-3 mr-1" />}
                          {mov.tipo === "EGRESO" && <ArrowDownCircle className="w-3 h-3 mr-1" />}
                          {mov.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell>{mov.concepto}</TableCell>
                      <TableCell className="font-mono text-sm">{mov.numero_referencia || "-"}</TableCell>
                      <TableCell className={"text-right font-bold " + (mov.tipo === "INGRESO" ? "text-green-600" : "text-red-600")}>
                        {mov.tipo === "EGRESO" && "-"}$ {parseFloat(mov.importe).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Plantillas */}
        <TabsContent value="plantillas" className="mt-6">
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-[#1E3876]">Plantillas de Operaciones</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Modelos predefinidos para movimientos frecuentes</p>
              </div>
              <Button onClick={() => { setPlantillaEditando(null); setFormPlantilla({ nombre: "", descripcion: "", tipo_operacion: "INGRESO", cuenta_tesoreria_id: null, cuenta_destino_id: null, cuenta_contable_debe_id: null, cuenta_contable_haber_id: null, concepto_default: "" }); setModalPlantilla(true); }} data-testid="btn-nueva-plantilla">
                <Plus className="w-4 h-4 mr-2" />Nueva Plantilla
              </Button>
            </CardHeader>
            <CardContent>
              {plantillas.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Copy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay plantillas definidas</p>
                  <p className="text-sm">Cree plantillas para operaciones frecuentes como cobranzas Visa, plazos fijos, FCI, etc.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Cuenta Tesoreria</TableHead>
                      <TableHead>Cuenta Debe</TableHead>
                      <TableHead>Cuenta Haber</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plantillas.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{p.nombre}</span>
                            {p.descripcion && <p className="text-xs text-gray-500">{p.descripcion}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={p.tipo_operacion === "INGRESO" ? "bg-green-100 text-green-700" : p.tipo_operacion === "EGRESO" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>
                            {p.tipo_operacion}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{p.cuenta_tesoreria_nombre || "-"}</TableCell>
                        <TableCell className="text-sm font-mono">{p.cuenta_debe_codigo ? p.cuenta_debe_codigo + " " + p.cuenta_debe_desc : "-"}</TableCell>
                        <TableCell className="text-sm font-mono">{p.cuenta_haber_codigo ? p.cuenta_haber_codigo + " " + p.cuenta_haber_desc : "-"}</TableCell>
                        <TableCell className="text-sm">{p.concepto_default || "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditPlantilla(p)} title="Editar">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeletePlantilla(p.id)} title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={modalCuenta} onOpenChange={setModalCuenta}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="text-[#1E3876]">Nueva Cuenta de Tesoreria</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={formCuenta.tipo} onValueChange={(v) => setFormCuenta({ ...formCuenta, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAJA">Caja</SelectItem>
                    <SelectItem value="BANCO">Banco</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Codigo *</Label>
                <Input value={formCuenta.codigo} onChange={(e) => setFormCuenta({ ...formCuenta, codigo: e.target.value })} placeholder="Ej: CAJA-01" />
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input value={formCuenta.nombre} onChange={(e) => setFormCuenta({ ...formCuenta, nombre: e.target.value })} placeholder="Nombre de la cuenta" />
            </div>
            <div>
              <Label>Moneda *</Label>
              <Select value={formCuenta.moneda_id} onValueChange={(v) => setFormCuenta({ ...formCuenta, moneda_id: v })}>
                <SelectTrigger data-testid="select-moneda"><SelectValue placeholder="Seleccionar moneda" /></SelectTrigger>
                <SelectContent>
                  {monedas.map((m) => (<SelectItem key={m.id} value={m.id.toString()}>{m.simbolo} {m.nombre} ({m.codigo})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {formCuenta.tipo === "BANCO" && (
              <>
                <div>
                  <Label>Banco</Label>
                  <Select value={formCuenta.banco_id?.toString()} onValueChange={(v) => setFormCuenta({ ...formCuenta, banco_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar banco" /></SelectTrigger>
                    <SelectContent>{bancos.map((b) => (<SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Numero de Cuenta</Label><Input value={formCuenta.numero_cuenta} onChange={(e) => setFormCuenta({ ...formCuenta, numero_cuenta: e.target.value })} /></div>
                  <div><Label>CBU</Label><Input value={formCuenta.cbu} onChange={(e) => setFormCuenta({ ...formCuenta, cbu: e.target.value })} maxLength={22} /></div>
                </div>
              </>
            )}
            <div><Label>Saldo Inicial</Label><Input type="number" value={formCuenta.saldo_inicial} onChange={(e) => setFormCuenta({ ...formCuenta, saldo_inicial: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalCuenta(false)}>Cancelar</Button>
            <Button onClick={handleSaveCuenta} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modalEditCuenta} onOpenChange={setModalEditCuenta}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="text-[#1E3876]">Editar Cuenta de Tesoreria</DialogTitle></DialogHeader>
          {cuentaEditar && (
            <div className="space-y-4 py-4">
              <div><Label>Codigo</Label><Input value={cuentaEditar.codigo} disabled className="bg-gray-100" /></div>
              <div><Label>Nombre</Label><Input value={cuentaEditar.nombre} onChange={(e) => setCuentaEditar({ ...cuentaEditar, nombre: e.target.value })} /></div>
              {cuentaEditar.tipo === "BANCO" && (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Numero de Cuenta</Label><Input value={cuentaEditar.numero_cuenta || ""} onChange={(e) => setCuentaEditar({ ...cuentaEditar, numero_cuenta: e.target.value })} /></div>
                  <div><Label>CBU</Label><Input value={cuentaEditar.cbu || ""} onChange={(e) => setCuentaEditar({ ...cuentaEditar, cbu: e.target.value })} maxLength={22} /></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalEditCuenta(false)}>Cancelar</Button>
            <Button onClick={handleSaveEditCuenta} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modalMovimiento} onOpenChange={setModalMovimiento}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="text-[#1E3876]">Nuevo Movimiento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {plantillas.length > 0 && (
              <div>
                <Label>Usar Plantilla</Label>
                <Select onValueChange={(v) => { const p = plantillas.find(pl => pl.id.toString() === v); if (p) handleAplicarPlantilla(p); }}>
                  <SelectTrigger data-testid="select-plantilla"><SelectValue placeholder="Seleccionar plantilla (opcional)" /></SelectTrigger>
                  <SelectContent>
                    {plantillas.map(p => (<SelectItem key={p.id} value={p.id.toString()}>{p.nombre} ({p.tipo_operacion})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cuenta *</Label>
                <Select value={formMovimiento.cuenta_tesoreria_id} onValueChange={(v) => setFormMovimiento({ ...formMovimiento, cuenta_tesoreria_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {cuentas.map((c) => {
                      const moneda = monedas.find(m => m.id === c.moneda_id) || { codigo: "ARS" };
                      return (<SelectItem key={c.id} value={c.id.toString()}>{c.nombre} ({moneda.codigo})</SelectItem>);
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formMovimiento.tipo} onValueChange={(v) => setFormMovimiento({ ...formMovimiento, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INGRESO">Ingreso</SelectItem>
                    <SelectItem value="EGRESO">Egreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fecha *</Label><Input type="date" value={formMovimiento.fecha} onChange={(e) => setFormMovimiento({ ...formMovimiento, fecha: e.target.value })} /></div>
              <div><Label>Importe *</Label><Input type="number" step="0.01" value={formMovimiento.importe} onChange={(e) => setFormMovimiento({ ...formMovimiento, importe: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Concepto *</Label><Input value={formMovimiento.concepto} onChange={(e) => setFormMovimiento({ ...formMovimiento, concepto: e.target.value })} placeholder="Descripcion del movimiento" /></div>
            <div><Label>Nro. Referencia</Label><Input value={formMovimiento.numero_referencia} onChange={(e) => setFormMovimiento({ ...formMovimiento, numero_referencia: e.target.value })} placeholder="Ej: RC-0001" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalMovimiento(false)}>Cancelar</Button>
            <Button onClick={handleSaveMovimiento} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear/Editar Plantilla */}
      <Dialog open={modalPlantilla} onOpenChange={setModalPlantilla}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader><DialogTitle className="text-[#1E3876]">{plantillaEditando ? "Editar Plantilla" : "Nueva Plantilla"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nombre *</Label><Input value={formPlantilla.nombre} onChange={(e) => setFormPlantilla({ ...formPlantilla, nombre: e.target.value })} placeholder="Ej: Cobranza Visa, Plazo Fijo, FCI" /></div>
            <div><Label>Descripcion</Label><Input value={formPlantilla.descripcion} onChange={(e) => setFormPlantilla({ ...formPlantilla, descripcion: e.target.value })} placeholder="Descripcion de la operacion" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Operacion *</Label>
                <Select value={formPlantilla.tipo_operacion} onValueChange={(v) => setFormPlantilla({ ...formPlantilla, tipo_operacion: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INGRESO">Ingreso</SelectItem>
                    <SelectItem value="EGRESO">Egreso</SelectItem>
                    <SelectItem value="TRANSFERENCIA">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cuenta Tesoreria</Label>
                <Select value={formPlantilla.cuenta_tesoreria_id?.toString() || "none"} onValueChange={(v) => setFormPlantilla({ ...formPlantilla, cuenta_tesoreria_id: v === "none" ? null : parseInt(v) })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin predefinir</SelectItem>
                    {cuentas.map(c => (<SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cuenta Contable al Debe</Label>
                <Select value={formPlantilla.cuenta_contable_debe_id?.toString() || "none"} onValueChange={(v) => setFormPlantilla({ ...formPlantilla, cuenta_contable_debe_id: v === "none" ? null : parseInt(v) })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin predefinir</SelectItem>
                    {cuentasContables.map(c => (<SelectItem key={c.id} value={c.id.toString()}>{c.codigo} - {c.descripcion}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cuenta Contable al Haber</Label>
                <Select value={formPlantilla.cuenta_contable_haber_id?.toString() || "none"} onValueChange={(v) => setFormPlantilla({ ...formPlantilla, cuenta_contable_haber_id: v === "none" ? null : parseInt(v) })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin predefinir</SelectItem>
                    {cuentasContables.map(c => (<SelectItem key={c.id} value={c.id.toString()}>{c.codigo} - {c.descripcion}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Concepto Predeterminado</Label><Input value={formPlantilla.concepto_default} onChange={(e) => setFormPlantilla({ ...formPlantilla, concepto_default: e.target.value })} placeholder="Concepto que se autocompletara" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPlantilla(false)}>Cancelar</Button>
            <Button onClick={handleSavePlantilla} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tesoreria;
